import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Calendar, Maximize2, X, ChevronLeft, ChevronRight, Images, Tag, Clock } from "lucide-react";
import { MaskLine } from "@/components/Reveal";
import { api, resolveImg } from "@/lib/api";

export default function Projects() {
  const { search } = useLocation();
  const [categories, setCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [active, setActive] = useState("All");
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null); // { photos, index }

  useEffect(() => {
    (async () => {
      try {
        const [cats, projs] = await Promise.all([api.get("/categories"), api.get("/projects")]);
        setCategories(cats.data);
        setProjects(projs.data);
        // Preselect the category from ?category=… if it matches an existing one.
        const requested = new URLSearchParams(search).get("category");
        if (requested) {
          const match = cats.data.find((c) => c.name.toLowerCase() === requested.toLowerCase());
          if (match) setActive(match.name);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [search]);

  const filtered = active === "All" ? projects : projects.filter((p) => p.category === active);

  const openLightbox = (photos, index) => setLightbox({ photos, index });
  const closeLightbox = () => setLightbox(null);
  const step = useCallback((dir) => {
    setLightbox((lb) => {
      if (!lb) return lb;
      const n = lb.photos.length;
      return { ...lb, index: (lb.index + dir + n) % n };
    });
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (!lightbox) return;
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, step]);

  return (
    <div data-testid="projects-page">
      <section className="bg-[#eaf6fc] pt-40 pb-24 relative overflow-hidden border-b border-black/10">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 relative z-10">
          <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#3da8d8] mb-8">Selected Work</p>
          <h1 className="font-display font-bold text-[#334155] text-5xl sm:text-7xl lg:text-8xl tracking-tight leading-[0.98]">
            <MaskLine delay={0.1}>Projects delivered</MaskLine>
            <MaskLine delay={0.25}>across <span className="text-[#3da8d8]">India.</span></MaskLine>
          </h1>
        </div>
      </section>

      {/* Filter */}
      <section className="sticky top-[64px] z-30 bg-[#F9FAFB]/90 backdrop-blur-md border-b border-black/10">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-5 flex gap-3 overflow-x-auto">
          {["All", ...categories.map((c) => c.name)].map((cat) => (
            <button
              key={cat}
              onClick={() => setActive(cat)}
              data-testid={`filter-${cat.replace(/\s+/g, "-").toLowerCase()}`}
              className={`whitespace-nowrap px-5 py-2.5 font-mono-accent text-[11px] uppercase tracking-widest border transition-colors duration-300 ${
                active === cat ? "bg-[#3da8d8] text-white border-[#3da8d8]" : "bg-transparent text-[#334155] border-black/15 hover:border-[#3da8d8]"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </section>

      {/* Grid */}
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-16 min-h-[50vh]">
        {loading ? (
          <p className="font-mono-accent text-xs uppercase tracking-widest text-[#52525B]">Loading projects…</p>
        ) : (
          <motion.div layout className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <AnimatePresence mode="popLayout">
              {filtered.map((p) => (
                <motion.article
                  layout
                  key={p.id}
                  data-testid={`project-card-${p.id}`}
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  className="group border border-black/10 bg-white overflow-hidden flex flex-col"
                >
                  <button
                    onClick={() => p.photos.length && openLightbox(p.photos, 0)}
                    data-testid={`project-gallery-${p.id}`}
                    className="relative overflow-hidden aspect-[4/3] block w-full"
                  >
                    {p.photos[0] ? (
                      <img src={resolveImg(p.photos[0])} alt={p.company_name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[900ms] ease-out" />
                    ) : (
                      <div className="w-full h-full bg-[#F9FAFB]" />
                    )}
                    <div className="absolute inset-0 bg-[#3da8d8]/0 group-hover:bg-[#3da8d8]/40 transition-colors duration-500 flex items-center justify-center">
                      <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-500 w-12 h-12 bg-white flex items-center justify-center">
                        <Maximize2 size={18} className="text-[#3da8d8]" />
                      </span>
                    </div>
                    {/* Bottom gradient overlay with click-to-view CTA */}
                    {p.photos.length > 0 && (
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent pt-16 pb-3 px-4 flex items-center justify-between text-white pointer-events-none">
                        <span className="font-mono-accent text-[10px] uppercase tracking-widest inline-flex items-center gap-2">
                          <Images size={13} className="text-[#78be27]" />
                          Click to view {p.photos.length > 1 ? "more" : "photo"}
                        </span>
                        <span className="font-mono-accent text-[10px] uppercase tracking-widest text-white/85">
                          {p.photos.length} photo{p.photos.length !== 1 ? "s" : ""}
                        </span>
                      </div>
                    )}
                  </button>

                  <div className="p-6 flex-1 flex flex-col">
                    <h3 className="font-display text-2xl leading-tight group-hover:text-[#3da8d8] transition-colors">{p.company_name}</h3>
                    <div className="mt-4 space-y-2 text-sm text-[#52525B]">
                      <p className="flex items-center gap-2"><MapPin size={14} className="text-[#3da8d8]" /> {p.location}</p>
                      <p className="flex items-center gap-2"><Calendar size={14} className="text-[#3da8d8]" /> {p.year}</p>
                      <p className="flex items-center gap-2"><Maximize2 size={14} className="text-[#3da8d8]" /> {p.area_sqft} sq ft</p>
                      {p.installation_days && String(p.installation_days).trim() !== "" && (
                        <p
                          data-testid={`project-installation-${p.id}`}
                          className="flex items-center gap-2"
                        >
                          <Clock size={14} className="text-[#3da8d8]" /> On site installation in {p.installation_days} day{String(p.installation_days).trim() === "1" ? "" : "s"}
                        </p>
                      )}
                      <p className="flex items-center gap-2 pt-1">
                        <Tag size={14} className="text-[#78be27]" />
                        <span
                          data-testid={`project-category-${p.id}`}
                          className="font-mono-accent text-[10px] uppercase tracking-widest text-[#78be27] border border-[#78be27]/40 bg-[#78be27]/10 px-2.5 py-1"
                        >
                          {p.category}
                        </span>
                      </p>
                    </div>
                  </div>
                </motion.article>
              ))}
            </AnimatePresence>
          </motion.div>
        )}
        {!loading && filtered.length === 0 && (
          <p className="font-mono-accent text-xs uppercase tracking-widest text-[#52525B]">No projects in this category yet.</p>
        )}
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="lightbox"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-white/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-10"
            onClick={closeLightbox}
          >
            <button onClick={closeLightbox} data-testid="lightbox-close" className="absolute top-6 right-6 text-[#475569] hover:text-[#3da8d8] z-10"><X size={30} /></button>
            <button onClick={(e) => { e.stopPropagation(); step(-1); }} data-testid="lightbox-prev" className="absolute left-4 sm:left-8 text-[#475569] hover:text-[#3da8d8] z-10"><ChevronLeft size={40} /></button>
            <button onClick={(e) => { e.stopPropagation(); step(1); }} data-testid="lightbox-next" className="absolute right-4 sm:right-8 text-[#475569] hover:text-[#3da8d8] z-10"><ChevronRight size={40} /></button>
            <motion.img
              key={lightbox.index}
              initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.35 }}
              src={resolveImg(lightbox.photos[lightbox.index])}
              alt="Project"
              onClick={(e) => e.stopPropagation()}
              className="max-h-[85vh] max-w-full object-contain border border-black/10"
            />
            <span className="absolute bottom-6 left-1/2 -translate-x-1/2 font-mono-accent text-xs text-[#475569]">
              {lightbox.index + 1} / {lightbox.photos.length}
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
